import type { FC } from 'react';
import { useRef } from 'react';
import Icon from '@components/Icon';
import { Show } from '@components/Show';
import { TooltipedIcon } from '@components/TooltipedIcon';
import MODALS from '@constants/modals';
import { BLOCK_ACTIONS_HOVERED_Z_INDEX, BLOCK_ACTIONS_Z_INDEX } from '@constants/z-indices';
import type { Attachment } from '@ContractBuilder/types';
import { Container, getId, iconClasses } from '@features/block-actions';
import { autoUpdate, limitShift, shift, useFloating } from '@floating-ui/react';
import { useModalDispatch } from '@hooks/useModalProvider';
import { mdiPencil, mdiTrashCanOutline } from '@mdi/js';
import clsx from 'clsx';

interface AttachmentItemProps {
  attachment: Attachment;
  withContextMenu?: boolean;
}

export const AttachmentItem: FC<AttachmentItemProps> = ({ attachment, withContextMenu = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { showModal } = useModalDispatch();
  const { refs, floatingStyles } = useFloating({
    placement: 'right-start',
    middleware: [
      shift({
        crossAxis: false,
        limiter: limitShift({
          offset: ({ rects }) => {
            return rects.floating.height;
          },
        }),
      }),
    ],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: containerRef?.current,
    },
  });

  const handleDelete = () =>
    showModal(MODALS.CONFIRM_DELETE, {
      id: attachment.id,
      title: attachment.name,
      type: 'attachment',
    });

  const handleEdit = () =>
    showModal(MODALS.ATTACHMENT_RENAME, {
      attachmentId: attachment.id,
      attachmentName: attachment.name,
      attachmentFileName: attachment.fileName,
    });

  return (
    <div className="group relative flex flex-col" ref={containerRef} id={attachment.id}>
      <div className="px-mrc-block-padding pointer-events-auto relative flex cursor-default border-b py-3 opacity-100 transition-colors duration-500">
        <div className="content-center p-2">
          <Icon name="paperclip" className="flex h-[20px] w-[20px] items-center hover:opacity-75" />
        </div>
        <p className="w-full overflow-hidden text-ellipsis">
          <span className="font-semibold">{attachment?.name ?? ''}</span>
          <br />
          <span className="text-slate-500">{attachment?.fileName ?? ''}</span>
        </p>
      </div>
      <Show when={withContextMenu}>
        <div
          className={clsx('py-1', BLOCK_ACTIONS_Z_INDEX, BLOCK_ACTIONS_HOVERED_Z_INDEX)}
          ref={refs.setFloating}
          style={floatingStyles}
        >
          <Container>
            <div className="flex h-fit flex-col gap-3 transition-all duration-300 ease-in-out">
              <TooltipedIcon
                size={0.9}
                className={iconClasses()}
                data-testid={getId('edit', attachment.name)}
                path={mdiPencil}
                content="Rename attachment"
                onClick={handleEdit}
              />
              <TooltipedIcon
                size={0.9}
                className={iconClasses()}
                data-testid={getId('delete', attachment.name)}
                path={mdiTrashCanOutline}
                content="Remove attachment"
                onClick={handleDelete}
              />
            </div>
          </Container>
        </div>
      </Show>
    </div>
  );
};
